import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { describeError, useToast } from "./toast";
import HelpIcon from "@mui/icons-material/HelpOutline";
import { useEffect, useState } from "react";
import type { CloneFileSummary } from "@shared/types/foyer";
import { cloneCompatibility } from "@shared/clone-compat";
import { foyerApi } from "../http/foyer";
import { errorText } from "../utils/apiError";
import { useFormat } from "../i18n/format";

/**
 * TEP cloning, step 3: push one of the premise's clones to this TV. A clone
 * that does not match the TV's reported model/firmware is shown with the
 * reason and a "?" holding the fix - the same rule the API enforces (409).
 */

/** The reason, with the fix behind a "?". */
export function MismatchReason({
  message,
  resolution,
}: {
  message: string;
  resolution: string;
}) {
  const { t } = useTranslation("premises");
  return (
    <Stack direction="row" spacing={0.5} alignItems="flex-start">
      <Typography variant="body2" color="error">
        {message}
      </Typography>
      <Tooltip
        title={<Typography variant="body2">{resolution}</Typography>}
        enterTouchDelay={0}
        leaveTouchDelay={8000}
      >
        <IconButton
          size="small"
          aria-label={t("restoreCloneDialog.howToFix")}
          sx={{ p: 0.25, mt: -0.25 }}
          data-testid="clone-mismatch-help"
        >
          <HelpIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export default function RestoreCloneDialog({
  open,
  premiseId,
  screenId,
  device,
  onClose,
  onPushed,
}: {
  open: boolean;
  premiseId: string;
  screenId: string;
  /** What the TV last reported - the left side of the match. */
  device: { model?: string | null; firmware?: string | null };
  onClose: () => void;
  /** Called with the command id once the push is queued. */
  onPushed: (commandId: string) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const [clones, setClones] = useState<CloneFileSummary[] | null>(null);
  const [error, setError] = useState<{
    message: string;
    resolution?: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setClones(null);
    foyerApi
      .listClones(premiseId)
      .then((r) => setClones(r.clones ?? []))
      .catch((e) => setError({ message: errorText(e, t) }));
  }, [open, premiseId, t]);

  async function push(c: CloneFileSummary) {
    setBusy(c.id);
    setError(null);
    try {
      const r = await foyerApi.restoreClone(premiseId, screenId, c.id);
      toast.success(t("restoreCloneDialog.queued", { firmware: c.firmware }));
      onPushed(r.commandId);
      onClose();
    } catch (e) {
      const body = (e as { body?: { message?: string; resolution?: string } })
        .body;
      setError({
        message: body?.message ?? (e instanceof Error ? e.message : String(e)),
        resolution: body?.resolution,
      });
      toast.error(
        body?.message
          ? t("common:couldNotBecause", {
              action: t("common:actions.restoreClone", {
                firmware: c.firmware,
              }),
              reason: body.message.replace(/[.\s]+$/, ""),
            })
          : describeError(t, "restoreClone", e, { firmware: c.firmware })
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("restoreCloneDialog.title")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("restoreCloneDialog.thisTv", {
            model: device.model ?? t("restoreCloneDialog.modelUnknown"),
            firmware:
              device.firmware ?? t("restoreCloneDialog.firmwareUnknown"),
          })}
        </Typography>
        {error &&
          (error.resolution ? (
            <Alert severity="error" icon={false} sx={{ mb: 1 }}>
              <MismatchReason
                message={error.message}
                resolution={error.resolution}
              />
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error.message}
            </Alert>
          ))}
        {clones === null && !error && (
          <Typography variant="body2">{t("common:states.loading")}</Typography>
        )}
        {clones?.length === 0 && (
          <Alert severity="info">{t("restoreCloneDialog.empty")}</Alert>
        )}
        <List dense>
          {clones?.map((c) => {
            const compat = cloneCompatibility(c, device);
            return (
              <ListItem
                key={c.id}
                divider
                secondaryAction={
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!compat.ok || busy !== null}
                    onClick={() => void push(c)}
                    data-testid="clone-restore-push"
                  >
                    {busy === c.id
                      ? t("restoreCloneDialog.sending")
                      : t("restoreCloneDialog.restore")}
                  </Button>
                }
              >
                <ListItemText
                  primary={`${c.model} · ${c.firmware}`}
                  secondary={
                    compat.ok ? (
                      t("restoreCloneDialog.matches", {
                        date: fmt.date(c.uploadedAt),
                      })
                    ) : (
                      <MismatchReason
                        message={compat.message}
                        resolution={compat.resolution}
                      />
                    )
                  }
                  secondaryTypographyProps={{ component: "div" }}
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common:buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
